"use server";

import { headers } from "next/headers";
import { z } from "zod";
import { checkRateLimit } from "@/lib/api/rate-limit";
import { clientIp } from "@/lib/auth/client-ip";
import { sendEmail } from "@/lib/email";
import { centralPrisma } from "@/lib/prisma/central";

const schema = z.object({
	name: z.string().trim().min(1).max(120),
	email: z.string().trim().toLowerCase().email().max(160),
	subject: z.string().trim().max(160).optional().default(""),
	message: z.string().trim().min(1).max(4000),
	locale: z.string().trim().max(5).optional().default("ar"),
	// Identifies WHICH Contact section instance this form belongs to, so the
	// notify address can be looked up server-side — never trust an email
	// address the client posts directly (TRY-unassigned adversary pass: the
	// old draft of this action took a `notifyEmail` field straight from
	// FormData and mailed it verbatim, which is an open relay — anyone could
	// submit the public form with an arbitrary destination and attacker-
	// controlled subject/body).
	sectionId: z.string().trim().max(64).optional().default(""),
});

export interface ContactState {
	ok?: boolean;
	error?: "invalid" | "rate";
}

/**
 * Public landing "Contact us" submission: stores the message centrally and
 * best-effort emails the platform contact address (or SUPERADMIN_EMAIL).
 */
export async function sendContactAction(
	_prev: ContactState,
	formData: FormData,
): Promise<ContactState> {
	const parsed = schema.safeParse(Object.fromEntries(formData));
	if (!parsed.success) return { error: "invalid" };
	const d = parsed.data;

	// This is an unauthenticated write plus an email send, and it had no bound at
	// all. Capacity engine (`rate-limit.ts`), not `attempt-limit.ts`: the
	// recipient is fixed (the platform contact address), so what this bounds is
	// spam rows and nuisance mail, not somebody else's invoice. The form renders
	// one generic error for any code, so `rate` needs no new copy.
	const ip = clientIp(await headers());
	if (!checkRateLimit(`contact:${ip}`, 5, 60 * 60_000).ok) return { error: "rate" };

	await centralPrisma.contactMessage.create({
		data: {
			name: d.name,
			email: d.email,
			subject: d.subject || null,
			message: d.message,
			locale: d.locale,
		},
	});

	// Best-effort notification — never fail the submission if email is down.
	try {
		// Resolved SERVER-SIDE from the block's own published content — the
		// client only tells us WHICH section, never the address itself.
		let sectionEmail: string | undefined;
		if (d.sectionId) {
			const block = await centralPrisma.landingSectionBlock.findUnique({
				where: { id: d.sectionId },
				select: { type: true, content: true },
			});
			if (block?.type === "contact") {
				const email = (block.content as { email?: unknown } | null)?.email;
				if (typeof email === "string" && email.trim()) sectionEmail = email.trim();
			}
		}
		const to = sectionEmail || process.env.SUPERADMIN_EMAIL;
		if (to) {
			void sendEmail({
				to,
				subject: `Jana contact form: ${d.subject || d.name}`,
				text: `From: ${d.name} <${d.email}>\n\n${d.message}`,
			});
		}
	} catch {
		/* ignore — message already saved */
	}

	return { ok: true };
}
